import React from "react";
import { useTranslation } from "react-i18next";
import { LAST_UPDATED, TERMS_VERSION, COMPANY_DETAILS_INCOMPLETE } from "../../constants/legal";
import LegalDocumentBody from "./LegalDocumentBody";
import { usePageMeta } from "../../hooks/usePageMeta";

/**
 * Presentational shell for the two legal documents, at their own addressable
 * URL (`/terms`, `/privacy`). This is the canonical copy — the one a footer
 * link, a support conversation, or an outside party can point to directly.
 * `LegalModal.jsx` shows the same content inline during signup, for someone
 * who does not want to leave the form to read it.
 *
 * A document that changes on its own schedule — the Privacy and Cookie
 * policies, which the Terms do not move with — carries its own `lastUpdated`;
 * the rest show the Terms' date. `children` render under the introduction, for
 * a document that needs a control next to it (the Cookie Policy's settings
 * button).
 *
 * Picks the language from i18n rather than a URL segment, so a reader who has
 * already chosen a language in the menu does not have to choose again. Romanian
 * is the fallback, and the governing version — the note saying so is part of the
 * document, not decoration.
 *
 * Public section, so Tailwind tokens only (see FRONTEND-PATTERNS.md).
 */
const LegalPage = ({ content, showVersion = false, children }) => {
  const { i18n } = useTranslation();
  const language = i18n?.language?.startsWith("en") ? "en" : "ro";
  const doc = content[language];
  // The title only: the route in App.jsx names the canonical URL.
  usePageMeta({ title: `${doc.title} | EasyPlanner` });

  return (
    <main className="legal-page mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
      <h1 className="legal-title text-fluid-xl font-bold tracking-tight text-ink-900">
        {doc.title}
      </h1>

      <p className="legal-meta mt-2 text-xs text-ink-400">
        {language === "ro" ? "Ultima actualizare" : "Last updated"}:{" "}
        {(content.lastUpdated ?? LAST_UPDATED)[language]}
        {showVersion ? ` · ${language === "ro" ? "versiunea" : "version"} ${TERMS_VERSION}` : ""}
      </p>

      {/* A placeholder left in a published legal document is a compliance
          problem, not a cosmetic one, so it is loud during development and
          absent from the production bundle. */}
      {COMPANY_DETAILS_INCOMPLETE && import.meta.env.DEV && (
        <p className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-fluid-sm text-amber-900">
          Datele de identificare ale societății sunt încă necompletate în{" "}
          <code className="font-mono text-xs">src/constants/legal.js</code>. Legea
          365/2002 le impune înainte de lansare.
        </p>
      )}

      <p className="mt-6 text-fluid-sm leading-relaxed text-ink-600">{doc.intro}</p>

      {children}

      <LegalDocumentBody sections={doc.sections} />
    </main>
  );
};

export default LegalPage;
